import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe } from 'lucide-react';
import { NAV_ITEMS, scrollToSection, type Lang } from '@/lib/index';

interface LayoutProps {
  children: React.ReactNode;
  lang: Lang;
  onLangChange: (lang: Lang) => void;
}

export default function Layout({ children, lang, onLangChange }: LayoutProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNav = (href: string) => {
    const id = href.replace('#', '');
    scrollToSection(id);
    setMobileOpen(false);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Skip link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md"
      >
        {lang === 'ca' ? 'Salta al contingut principal' : 'Saltar al contenido principal'}
      </a>

      {/* ── Header ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-sm shadow-sm border-b border-border'
            : 'bg-transparent'
        }`}
        role="banner"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <button
              onClick={() => scrollToSection('hero')}
              className="flex items-center gap-3 group focus:outline-none focus:ring-2 focus:ring-accent rounded-lg p-1"
              aria-label="Liberum – Inici"
            >
              <div className="h-10 lg:h-12 overflow-hidden flex items-center justify-center shrink-0">
                <img 
                  src="/images/logo_brand.png" 
                  alt="Liberum Logo" 
                  className="h-full w-auto object-contain"
                />
              </div>
            </button>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1" role="navigation" aria-label="Navegació principal">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.href}
                  onClick={() => handleNav(item.href)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-accent ${
                    scrolled ? 'text-foreground hover:bg-muted hover:text-primary' : 'text-white/90 hover:text-white'
                  }`}
                >
                  {lang === 'ca' ? item.label : item.labelEs}
                </button>
              ))}
            </nav>

            {/* Right side: lang + CTA + menu */}
            <div className="flex items-center gap-3">
              {/* Language selector */}
              <button
                onClick={() => onLangChange(lang === 'ca' ? 'es' : 'ca')}
                className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-accent ${
                  scrolled
                    ? 'border-border text-muted-foreground hover:border-accent hover:text-accent'
                    : 'border-white/40 text-white/80 hover:border-white hover:text-white'
                }`}
                aria-label={lang === 'ca' ? 'Canviar a castellà' : 'Cambiar a catalán'}
              >
                <Globe size={12} aria-hidden="true" />
                <span>{lang === 'ca' ? 'ES' : 'CA'}</span>
              </button>

              {/* CTA button – desktop */}
              <button
                onClick={() => scrollToSection('contact')}
                className="hidden lg:flex items-center px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:opacity-90 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                style={{ background: 'linear-gradient(135deg, #0A1F44 0%, #2A9D8F 100%)' }}
              >
                {lang === 'ca' ? 'Reserva ara' : 'Reservar ahora'}
              </button>

              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className={`lg:hidden p-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-accent ${
                  scrolled ? 'text-foreground hover:bg-muted' : 'text-white hover:bg-white/10'
                }`}
                aria-expanded={mobileOpen}
                aria-label={mobileOpen ? 'Tancar menú' : 'Obrir menú'}
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className="lg:hidden bg-white border-t border-border shadow-lg overflow-hidden"
              role="navigation"
              aria-label="Menú mòbil"
            >
              <div className="container mx-auto px-4 py-4 flex flex-col gap-1">
                {NAV_ITEMS.map((item) => (
                  <button
                    key={item.href}
                    onClick={() => handleNav(item.href)}
                    className="text-left px-4 py-3 rounded-lg text-sm font-medium text-foreground hover:bg-muted hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-accent"
                  >
                    {lang === 'ca' ? item.label : item.labelEs}
                  </button>
                ))}
                <div className="pt-2 border-t border-border mt-2">
                  <button
                    onClick={() => { scrollToSection('contact'); setMobileOpen(false); }}
                    className="w-full py-3 px-4 rounded-xl text-sm font-semibold text-white text-center focus:outline-none focus:ring-2 focus:ring-accent"
                    style={{ background: 'linear-gradient(135deg, #0A1F44 0%, #2A9D8F 100%)' }}
                  >
                    {lang === 'ca' ? 'Reserva les teves vacances' : 'Reserva tus vacaciones'}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ── Main Content ── */}
      <main id="main-content" tabIndex={-1}>
        {children}
      </main>

      {/* ── Footer ── */}
      <footer className="bg-primary text-primary-foreground" role="contentinfo">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <div className="h-10 overflow-hidden flex items-center justify-center bg-white/10 p-1 rounded">
                  <img 
                    src="/images/logo_brand.png" 
                    alt="Liberum Logo" 
                    className="h-full w-auto object-contain brightness-0 invert"
                  />
                </div>
              </div>
              <p className="text-white/70 text-sm leading-relaxed mb-4">
                {lang === 'ca'
                  ? 'Cooperativa de turisme accessible a Mallorca. Creiem que tothom mereix gaudir d\'unes vacances dignes, independentment de les seves capacitats.'
                  : 'Cooperativa de turismo accesible en Mallorca. Creemos que todos merecen disfrutar de unas vacaciones dignas, independientemente de sus capacidades.'}
              </p>
              <div className="flex gap-3">
                <a href="https://www.instagram.com/liberuum_?igsh=OXV4NjBlY2hpY2Uy&utm_source=qr" target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-accent"
                  aria-label="Instagram de Liberum">
                  <span className="text-sm" aria-hidden="true">📷</span>
                </a>
                <a href="https://facebook.com/liberumvacances" target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-accent"
                  aria-label="Facebook de Liberum">
                  <span className="text-sm" aria-hidden="true">👤</span>
                </a>
              </div>
            </div>

            {/* Links */}
            <div>
              <h3 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">
                {lang === 'ca' ? 'Navegació' : 'Navegación'}
              </h3>
              <ul className="space-y-2">
                {NAV_ITEMS.map((item) => (
                  <li key={item.href}>
                    <button
                      onClick={() => handleNav(item.href)}
                      className="text-white/70 hover:text-white text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-accent rounded"
                    >
                      {lang === 'ca' ? item.label : item.labelEs}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">
                {lang === 'ca' ? 'Contacte' : 'Contacto'}
              </h3>
              <ul className="space-y-3 text-white/70 text-sm">
                <li className="flex items-start gap-2">
                  <span aria-hidden="true" className="mt-0.5">📍</span>
                  <span>Carrer 9, Polígon Industrial Picapedrer<br />Pollença, Illes Balears 07460</span>
                </li>
                <li className="flex items-center gap-2">
                  <span aria-hidden="true">📞</span>
                  <a href="tel:+34971000000" className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-accent rounded">
                    +34 971 000 000
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <span aria-hidden="true">✉️</span>
                  <a href="mailto:info@liberum.coop" className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-accent rounded">
                    info@liberum.coop
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <span aria-hidden="true">💬</span>
                  <a href="https://wa.me/34600000000" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-accent rounded">
                    WhatsApp
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 mt-8 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-xs">
              © 2026 Liberum – Cooperativa de Turisme Accessible. {lang === 'ca' ? 'Tots els drets reservats.' : 'Todos los derechos reservados.'}
            </p>
            <p className="text-white/40 text-xs">
              {lang === 'ca' ? 'Dissenyat amb ❤️ per un turisme inclusiu' : 'Diseñado con ❤️ por un turismo inclusivo'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
